import { ElementProps } from '@/components/sections/builder/types';
import {
  ImageBannerButtonGroupElementDataType,
  ImageBannerElementDataType,
} from '@/components/sections/builder/elements/body/image-banner/types';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import { shallow } from 'zustand/shallow';
import { GalleryHorizontal } from 'lucide-react';
import InputLink from '@/components/sections/builder/settings/input-link';
import Link from 'next/link';

const defaultData: ImageBannerButtonGroupElementDataType = {
  name: 'Button Group',
  button1Label: 'Shop All',
  button1Type: 'secondary',
  gap: 12,
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as ImageBannerButtonGroupElementDataType;
  const parentData = element.parentData as ImageBannerElementDataType;

  const isExternal1 = data.button1Link?.includes('http');
  const isExternal2 = data.button2Link?.includes('http');

  return (
    <div
      className={clsx(
        'button-padding my-4 flex w-full flex-col',
        parentData.contentAlign === 'left' && 'md:items-start',
        parentData.contentAlign === 'center' && 'md:items-center',
        parentData.contentAlign === 'right' && 'md:items-end',
        parentData.contentAlignMobile === 'left' && 'items-start',
        parentData.contentAlignMobile === 'center' && 'items-center',
        parentData.contentAlignMobile === 'right' && 'items-end'
      )}
      data-animation='true'
    >
      <div className='cols_wrapper button-gap grid w-fit break-all'>
        {!!data.button1Label && (
          <Link
            href={
              isExternal1
                ? data.button1Link || '#'
                : `/${(data.button1Link || '').replace(/^\/+/, '')}`
            }
            className={clsx(
              'button-gap theme_button',
              parentData.containerColorScheme &&
                `button_container_${data.button1Type === 'primary' ? 'solid' : 'outline'}`,
              data.button1Type === 'primary'
                ? 'focus:ring-primary-300 inline-flex items-center justify-center rounded-lg bg-primary px-5 py-3 text-center text-base font-medium text-white hover:bg-primary focus:ring-4'
                : 'inline-flex items-center justify-center rounded-lg border border-gray-300 px-5 py-3 text-center text-base font-medium text-gray-900 outline hover:bg-gray-100 focus:ring-4 focus:ring-gray-100'
            )}
            data-animation='true'
            target={isExternal1 ? '_blank' : undefined}
            rel={isExternal1 ? 'noopener noreferrer' : undefined}
          >
            {data.button1Label}
          </Link>
        )}
        {!!data.button2Label && (
          <Link
            href={
              isExternal2
                ? data.button2Link || '#'
                : `/${(data.button2Link || '').replace(/^\/+/, '')}`
            }
            className={clsx(
              'button-gap theme_button',
              parentData.containerColorScheme &&
                `button_container_${data.button2Type === 'primary' ? 'solid' : 'outline'}`,
              data.button2Type === 'primary'
                ? 'focus:ring-primary-300 inline-flex items-center justify-center rounded-lg bg-primary px-5 py-3 text-center text-base font-medium text-white hover:bg-primary focus:ring-4'
                : 'inline-flex items-center justify-center rounded-lg border border-gray-300 px-5 py-3 text-center text-base font-medium text-gray-900 outline hover:bg-gray-100 focus:ring-4 focus:ring-gray-100'
            )}
            data-animation='true'
            target={isExternal2 ? '_blank' : undefined}
            rel={isExternal2 ? 'noopener noreferrer' : undefined}
          >
            {data.button2Label}
          </Link>
        )}
        <style jsx>
          {`
            .cols_wrapper {
              grid-template-columns: repeat(
                ${[data.button1Label, data.button2Label].filter((v) => !!v).length},
                minmax(0, 1fr)
              );
            }
            .button-gap {
              gap: ${data.gap || 0}px;
            }
            @media (min-width: 1024px) {
              .button-padding {
                padding-top: ${data.padding?.top || 0}px;
                padding-bottom: ${data.padding?.bottom || 0}px;
                padding-left: ${data.padding?.left || 0}px;
                padding-right: ${data.padding?.right || 0}px;
              }
            }

            .button_container_solid {
              background-color: ${parentData.containerColorScheme?.btnSolidBackgroud} !important;
              color: ${parentData.containerColorScheme?.btnSolidlabel} !important;
              transition: opacity 0.2s ease-in-out !important;
            }

            .button_container_solid:hover {
              opacity: 0.8 !important;
            }

            .button_container_outline {
              background-color: transparent;
              color: ${parentData.containerColorScheme?.btnOutline} !important;
              border: 2px solid ${parentData.containerColorScheme?.btnOutline} !important;
              transition: all 0.2s ease-in-out !important;
            }

            .button_container_outline:hover {
              background-color: ${parentData.containerColorScheme?.btnOutline} !important;
              color: ${parentData.containerColorScheme?.btnSolidlabel} !important;
            }
          `}
        </style>
      </div>
    </div>
  );
};

export const ElementPreviewIcon = GalleryHorizontal;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = (element.data || defaultData) as ImageBannerButtonGroupElementDataType;

  return (
    <div>
      <div className='mb-4 font-bold'>First button label</div>
      <Input
        onChange={(e) =>
          builder.setChildData(element.parentId as string, element.id, {
            button1Label: e.target.value,
          })
        }
        name='button1Label'
        type='text'
        size={100}
        defaultValue={data.button1Label}
        className='w-full'
        placeholder='Label'
      />
      <span className='mt-0.5'>Leave the label blank to hide the button</span>
      <div className='mb-4 mt-4 font-bold'>First button link</div>
      <InputLink
        name='button1Link'
        placeholder='Search or enter a link'
        onChange={(value) => {
          builder.setChildData(element.parentId as string, element.id, {
            button1Link: value,
          });
        }}
        value={data.button1Link as string}
      />
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='button1Type'
          className='mx-2'
          checked={data.button1Type === 'secondary'}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              button1Type: checked ? 'secondary' : 'primary',
            });
          }}
        />
        <label htmlFor='showContainer'>Use outline button style</label>
      </div>
      <div className='mb-4 mt-4 font-bold'>Second button label</div>
      <Input
        onChange={(e) =>
          builder.setChildData(element.parentId as string, element.id, {
            button2Label: e.target.value,
          })
        }
        name='button2Label'
        type='text'
        size={100}
        defaultValue={data.button2Label}
        className='w-full'
        placeholder='Label'
      />
      <span className='mt-0.5'>Leave the label blank to hide the button</span>
      <div className='mb-4 mt-4 font-bold'>Second button link</div>
      <InputLink
        name='button2Link'
        placeholder='Search or enter a link'
        onChange={(value) => {
          builder.setChildData(element.parentId as string, element.id, {
            button2Link: value,
          });
        }}
        value={data.button2Link as string}
      />
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='button2Type'
          className='mx-2'
          checked={data.button2Type === 'secondary'}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              button2Type: checked ? 'secondary' : 'primary',
            });
          }}
        />
        <label htmlFor='showContainer'>Use outline button style</label>
      </div>
      <div className='mb-4 mt-4 font-bold'>Button Gap</div>
      <InputSlider
        value={data.gap || 0}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            gap: val,
          })
        }
      />
    </div>
  );
}

const ImageBannerButtonGroup = {
  type: 'image_banner_button_group',
  group: 'image_banner',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default ImageBannerButtonGroup;
